/**
 * product-parts.js
 * Parts & Accessories page — Zebra Skimmers Store
 *
 * Responsibilities:
 *  - Filter grid by type tab (all / part / accessory)
 *  - Filter grid by compatibility chip
 *  - Variant pill selection → updates hidden ID, price, button state
 *  - AJAX add-to-cart via /cart/add.js
 *  - Sticky cart bar updates
 *  - GA4 add_to_cart tracking via existing GA4EcommerceTracker
 *  - ARIA live region announcements
 */

(function () {
  'use strict';

  class ProductPartsPage {
    constructor(section) {
      this.section      = section;
      this.grid         = section.querySelector('#PartsGrid');
      this.cartBar      = section.querySelector('#PartsCartBar');
      this.cartCount    = section.querySelector('#PartsCartCount');
      this.cartTotal    = section.querySelector('#PartsCartTotal');
      this.liveRegion   = section.querySelector('#PartsCartStatus');
      this.loadMoreWrap = section.querySelector('#PartsLoadMore');
      this.loadMoreBtn  = section.querySelector('.parts-grid__load-more');
      this.contactPage  = section.dataset.contactPage || '/pages/contact-us';
      this.enableAjax   = section.dataset.enableAjax !== 'false';

      this.activeTab    = 'all';
      this.activeCompat = null;
      this.scannedModelHandle = null;
      this.cartItems    = 0;
      this.cartTotalCents = 0;

      this.scanNotice   = section.querySelector('#PartsScanNotice');

      this._bindFilters();
      this._bindVariantSelectors();
      this._bindForms();
      this._bindLoadMore();
      this._bindScanFilterClear();
      this._updateTabCounts();
      this._autoFilterFromQrScan();
    }

    /* ---- Filter binding ------------------------------------ */

    _bindFilters() {
      const tabs = this.section.querySelectorAll('[data-parts-tabs] [data-tab]');
      tabs.forEach(btn => {
        btn.addEventListener('click', () => {
          tabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-pressed', 'false'); });
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
          this.activeTab = btn.dataset.tab;
          this._applyFilters();
        });
      });

      const chips = this.section.querySelectorAll('[data-parts-compat] [data-compat]');
      chips.forEach(chip => {
        chip.addEventListener('click', () => {
          chips.forEach(c => { c.classList.remove('is-active'); c.setAttribute('aria-pressed', 'false'); });
          chip.classList.add('is-active');
          chip.setAttribute('aria-pressed', 'true');

          if (chip.dataset.compat === 'all') {
            this.activeCompat = null;
            this._clearScanFilter();
          } else {
            this.activeCompat = chip.dataset.compat;
          }
          this._applyFilters();
        });
      });
    }

    /* Compat chip filter (user-selected or auto-selected from QR scan),
       with a QR scan fallback when no compat chip matched the scanned model. */
    _passesCompatAndScan(item) {
      const compatStr   = item.dataset.compat || '';
      const compatTokens = compatStr.split(' ').filter(Boolean);

      const compatMatch = !this.activeCompat || compatTokens.includes(this.activeCompat);

      let scanMatch = true;
      if (this.scannedModelHandle && !this.activeCompat) {
        scanMatch = compatTokens.some(token =>
          token === this.scannedModelHandle ||
          token.includes(this.scannedModelHandle) ||
          this.scannedModelHandle.includes(token)
        );
      }
      return compatMatch && scanMatch;
    }

    _applyFilters() {
      if (!this.grid) return;
      const items = this.grid.querySelectorAll('.parts-grid__item');
      let visible = 0;

      items.forEach(item => {
        const typeMatch = this.activeTab === 'all' || item.dataset.type === this.activeTab;
        const show = typeMatch && this._passesCompatAndScan(item);
        item.hidden = !show;
        if (show) visible++;
      });

      this._updateTabCounts();
      this._announce(visible + ' item' + (visible !== 1 ? 's' : '') + ' shown');
    }

    _updateTabCounts() {
      if (!this.grid) return;
      const items = Array.from(this.grid.querySelectorAll('.parts-grid__item'));

      // Count items that pass the compat + scan filters (ignoring tab selection)
      // so each tab badge shows an accurate preview of what it contains.
      const eligible = items.filter(item => this._passesCompatAndScan(item));

      const set = (key, val) => {
        const el = this.section.querySelector('[data-tab-count="' + key + '"]');
        if (el) el.textContent = val;
      };
      set('all',       eligible.length);
      set('part',      eligible.filter(i => i.dataset.type === 'part').length);
      set('accessory', eligible.filter(i => i.dataset.type === 'accessory').length);
    }

    /* ---- QR scan auto-filter ------------------------------- */

    _autoFilterFromQrScan() {
      const scannedModel = this.section.dataset.scannedModel;
      if (!scannedModel) return;

      // Try exact match first against compat chip handles
      const chips = this.section.querySelectorAll('[data-parts-compat] [data-compat]');
      let matched = null;

      if (chips.length) {
        for (const chip of chips) {
          if (chip.dataset.compat === 'all') continue;
          if (chip.dataset.compat === scannedModel) {
            matched = chip;
            break;
          }
        }

        // Fallback: partial match — check if the scanned model handle
        // appears within a chip value or vice versa
        if (!matched) {
          for (const chip of chips) {
            if (chip.dataset.compat === 'all') continue;
            if (chip.dataset.compat.includes(scannedModel) || scannedModel.includes(chip.dataset.compat)) {
              matched = chip;
              break;
            }
          }
        }
      }

      if (matched) {
        // Activate the matched chip
        chips.forEach(c => {
          c.classList.remove('is-active');
          c.setAttribute('aria-pressed', 'false');
        });
        matched.classList.add('is-active');
        matched.setAttribute('aria-pressed', 'true');
        this.activeCompat = matched.dataset.compat;
        this._applyFilters();
      } else {
        // No compat chip match — fall back to filtering grid items
        // directly by checking if any item's compat data contains
        // the scanned model handle
        this._filterByScannedModel(scannedModel);
      }
    }

    _clearScanFilter() {
      this.scannedModelHandle = null;
      if (this.scanNotice) this.scanNotice.hidden = true;
    }

    _bindScanFilterClear() {
      const clearBtn = this.section.querySelector('[data-clear-scan-filter]');
      if (!clearBtn) return;
      clearBtn.addEventListener('click', () => {
        this._clearScanFilter();
        this.activeCompat = null;

        // Reset compat chips to none-active state
        const chips = this.section.querySelectorAll('[data-parts-compat] [data-compat]');
        chips.forEach(c => {
          c.classList.remove('is-active');
          c.setAttribute('aria-pressed', 'false');
        });
        // Activate the "All models" chip if it exists
        const allChip = this.section.querySelector('[data-parts-compat] [data-compat="all"]');
        if (allChip) {
          allChip.classList.add('is-active');
          allChip.setAttribute('aria-pressed', 'true');
        }

        this._applyFilters();
      });
    }

    _filterByScannedModel(modelHandle) {
      if (!this.grid) return;
      const items = this.grid.querySelectorAll('.parts-grid__item');
      let hasMatch = false;

      items.forEach(item => {
        const compatStr = item.dataset.compat || '';
        const compatTokens = compatStr.split(' ').filter(Boolean);
        const isMatch = compatTokens.some(token =>
          token === modelHandle ||
          token.includes(modelHandle) ||
          modelHandle.includes(token)
        );
        if (isMatch) hasMatch = true;
      });

      // Only persist the scan filter if at least one part matches,
      // otherwise leave the grid unfiltered.
      if (hasMatch) {
        this.scannedModelHandle = modelHandle;
      }

      this._applyFilters();
    }

    /* ---- Variant selectors --------------------------------- */

    _bindVariantSelectors() {
      this.section.querySelectorAll('[data-variant-selector]').forEach(sel => {
        const productId = sel.dataset.variantSelector;
        const variantsEl = sel.querySelector('[data-variants="' + productId + '"]');
        if (!variantsEl) return;

        let variants;
        try { variants = JSON.parse(variantsEl.textContent); } catch (_) { return; }

        const card    = sel.closest('.card-part');
        const pills   = sel.querySelectorAll('.card-part__variant-pill');
        const groups  = sel.querySelectorAll('.card-part__variant-group');

        // Track current selections
        const selections = {};
        groups.forEach(g => {
          const pos = g.querySelector('.card-part__variant-pill')?.dataset.optionPosition;
          const selectedPill = g.querySelector('.card-part__variant-pill.is-selected');
          if (pos && selectedPill) selections[pos] = selectedPill.dataset.optionValue;
        });

        pills.forEach(pill => {
          pill.addEventListener('click', () => {
            const pos = pill.dataset.optionPosition;
            const val = pill.dataset.optionValue;

            // Deselect siblings in same group
            sel.querySelectorAll('.card-part__variant-pill[data-option-position="' + pos + '"]')
              .forEach(p => { p.classList.remove('is-selected'); p.setAttribute('aria-pressed', 'false'); });

            pill.classList.add('is-selected');
            pill.setAttribute('aria-pressed', 'true');
            selections[pos] = val;

            // Find matching variant
            const match = variants.find(v => {
              return v.options.every((opt, i) => selections[String(i + 1)] === opt);
            });

            if (match && card) this._updateCard(card, match);
          });
        });
      });
    }

    _updateCard(card, variant) {
      // Hidden ID in form
      const idInput = card.querySelector('.js-variant-id');
      if (idInput) idInput.value = variant.id;

      // Price
      const priceEl = card.querySelector('.card-part__price');
      if (priceEl) {
        const compare = variant.compare_at_price > variant.price
          ? '<span class="card-part__price-compare">' + this._money(variant.compare_at_price) + '</span> '
          : '';
        priceEl.innerHTML = compare + this._money(variant.price);
      }

      // Update contact link
      const link = card.querySelector('.card-part__contact-link');
      if (link) {
        const url = new URL(link.href);
        url.searchParams.set('variant', variant.id);
        link.href = url.toString();
      }

      // Button state
      const btn = card.querySelector('.card-part__add-btn');
      if (btn) {
        btn.disabled = !variant.available;
        const label = variant.available ? 'Add to cart' : 'Out of stock';
        btn.querySelector('span:not(.spinner-xs)').textContent = label;
      }
    }

    /* ---- AJAX forms ---------------------------------------- */

    _bindForms() {
      if (!this.enableAjax) return;

      this.section.addEventListener('submit', e => {
        const form = e.target.closest('.card-part__form');
        if (!form || form.dataset.ajax === 'false') return;
        e.preventDefault();
        this._addToCart(form);
      });
    }

    async _addToCart(form) {
      const btn    = form.querySelector('.card-part__add-btn');
      const idInput = form.querySelector('.js-variant-id');
      if (!idInput) return;

      const body = { id: idInput.value, quantity: 1 };

      // Collect line item properties
      const props = {};
      form.querySelectorAll('input[name^="properties["]').forEach(inp => {
        const key = inp.name.replace(/properties\[(.+)\]/, '$1');
        props[key] = inp.value;
      });
      if (Object.keys(props).length) body.properties = props;

      if (btn) btn.classList.add('is-loading');

      try {
        const resp = await fetch(window.Shopify.routes?.root + 'cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify(body)
        });

        if (!resp.ok) throw new Error('Cart error ' + resp.status);

        const item = await resp.json();
        this._announce(item.title + ' added to cart');
        this._refreshCartBar();
        this._fireGA4(item);

      } catch (err) {
        this._announce('Could not add item — please try again');
        console.error('[product-parts]', err);
      } finally {
        if (btn) btn.classList.remove('is-loading');
      }
    }

    async _refreshCartBar() {
      try {
        const resp = await fetch(window.Shopify.routes?.root + 'cart.js', { headers: { Accept: 'application/json' } });
        const cart = await resp.json();
        this.cartItems = cart.item_count;
        this.cartTotalCents = cart.total_price;

        if (this.cartBar) {
          this.cartBar.hidden = cart.item_count === 0;
          if (this.cartCount) this.cartCount.textContent = cart.item_count + ' item' + (cart.item_count !== 1 ? 's' : '');
          if (this.cartTotal) this.cartTotal.textContent = this._money(cart.total_price);
        }
      } catch (_) {}
    }

    _fireGA4(item) {
      if (typeof GA4EcommerceTracker !== 'undefined') {
        try {
          GA4EcommerceTracker.trackAddToCart({
            item_id:    item.variant_id || item.id,
            item_name:  item.title,
            price:      (item.price / 100).toFixed(2),
            quantity:   item.quantity,
            item_category: 'Parts & Accessories'
          });
        } catch (_) {}
      }
    }

    /* ---- Load more ----------------------------------------- */

    _bindLoadMore() {
      if (!this.loadMoreBtn) return;
      this.loadMoreBtn.addEventListener('click', () => {
        // All items are server-rendered; "load more" just unhides hidden LIs
        const hidden = this.grid.querySelectorAll('.parts-grid__item[data-deferred]');
        hidden.forEach(item => { item.removeAttribute('data-deferred'); item.hidden = false; });
        if (this.loadMoreWrap) this.loadMoreWrap.hidden = true;
        this._applyFilters();
      });
    }

    /* ---- Helpers ------------------------------------------- */

    _money(cents) {
      return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    _announce(msg) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      requestAnimationFrame(() => { this.liveRegion.textContent = msg; });
    }
  }

  // Init on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-section-type="main-product-parts"]').forEach(section => {
      new ProductPartsPage(section);
    });
  });

})();
